import React from 'react';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LabDetails from 'components/Modal/LabDetails/LabDetails';
import { labsQuery } from 'pages/PatientRecords/PatientRecords';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import Skeleton from 'react-loading-skeleton';
import Graphic from 'components/Graphic/Graphic';
import { paths } from '../../routes';
import { LabTest } from '../../types/types';
import { LAB_TEST_STATUS } from '../../context/visitContext';
import {
  formatArrayIntoSentence,
  hasOpenedLabReport,
  moveFilteredItemsToTop,
} from '../../utils/utils';
import Icon from '../../components/Icon/Icon';
import { usePatient } from '../../context/appContext';

function Labs() {
  const { patient } = usePatient();
  const [labsLimit, setLabsLimit] = React.useState(6);
  const [labDetails, setLabDetails] = React.useState<LabTest>();
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, isFetching } = useQuery({
    ...labsQuery({ patientId: patient.id }),
    enabled: !!patient.id,
  });

  let labs = (data || []) as LabTest[];

  labs = moveFilteredItemsToTop({
    array: labs,
    findFunction: item =>
      item.questionnaireId === visitId && item.status === LAB_TEST_STATUS.RECEIVED_REPORT,
  });

  const isNewLabReport = (lab: LabTest) => {
    return lab.questionnaireId === visitId && !hasOpenedLabReport([lab]);
  };

  const handleLabTestClicked = (lab: LabTest) => {
    if (!(lab.status === LAB_TEST_STATUS.RECEIVED_REPORT && lab.url)) {
      setLabDetails(lab);
      return;
    }

    const params = isNewLabReport(lab)
      ? `?visitId=${lab.questionnaireId}&search=${encodeURIComponent(location.search)}`
      : '';

    // navigate(`${window.location.origin}${paths.LAB_RESULTS_PATH.replace(':labTestId', lab.id || '')}`);
    window.location.href = `${window.location.origin}${paths.LAB_RESULTS_PATH.replace(
      ':labTestId',
      lab.id || '',
    )}${params}`;
  };

  const handleNextTab = () => {
    const tab = 'labs';

    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === tab);

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname.replace(tab, nextTab.id)}${location.search}`;
      navigate(tabURL);
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col space-y-2">
      <div className="laptop:grid grid-cols-7 gap-6 pt-2 pb-4 text-[15px] font-medium hidden">
        <div className="col-span-2">
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div>
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
        <div className="col-span-2">
          <Skeleton width="100%" height={18} duration={1.4} />
        </div>
      </div>

      {new Array(4).fill(null).map((_, index) => (
        <div
          className="laptop:grid laptop:grid-cols-7 gap-6 justify-between py-1 items-center"
          key={index}
        >
          <div className="col-span-2">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="col-span-2 hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="flex flex-col pt-0 pb-3 tablet:pt-5 tablet:pb-1">
        {(isLoading && isFetching) || !labs ? (
          <>{renderLoading()}</>
        ) : labs.length === 0 ? (
          <div className="flex flex-col justify-center items-center mt-8 mb-20">
            <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
            <div className="text-center mt-3">
              <p className="font-medium">You’re all caught up.</p>
              <p className="text-[#7A7A7A] text-xs">
                {`${patient.firstName} ${patient.lastName}`} has no previous lab orders.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="laptop:grid grid-cols-5 gap-6 pt-2 pb-3 text-[15px] font-semibold hidden">
              <div className="col-span-2">Lab name</div>
              <div>Date</div>
              <div>Status</div>
              <div>See more</div>
            </div>

            <div className="fs-exclude flex flex-col space-y-3">
              {labs.slice(0, labsLimit).map(lab => (
                <div
                  role="presentation"
                  onClick={() => handleLabTestClicked(lab)}
                  className="hover:bg-[#F6FAFF] px-4 rounded-full -mx-4 py-1 cursor-pointer"
                  key={lab.id}
                >
                  <div
                    className={`laptop:grid laptop:grid-cols-5 gap-6 flex justify-between py-1 items-center ${
                      isNewLabReport(lab) ? 'text-river-blue' : ''
                    } `}
                  >
                    <div className="col-span-2 flex items-center laptop:space-x-8 space-x-3">
                      <div>
                        <div className="text-sm">
                          {formatArrayIntoSentence(lab.types.map((lap: LabTest) => lap.name))}
                        </div>
                        <div
                          className="mobile:text-stone-500 mobile:mt-1 flex items-center font-normal 
                          text-sm laptop:hidden"
                        >
                          {moment(lab.createdAt, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                        </div>
                      </div>
                    </div>

                    <div className="laptop:flex items-center text-sm hidden">
                      {moment(lab.createdAt, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                    </div>

                    <div
                      title={
                        lab.status === LAB_TEST_STATUS.REQUESTED
                          ? 'Requested'
                          : lab.status === LAB_TEST_STATUS.RECEIVED_REPORT && lab.url
                          ? 'Received report'
                          : 'Sent to lab'
                      }
                      className={`${
                        lab.status === LAB_TEST_STATUS.RECEIVED_REPORT && lab.url
                          ? 'bg-gray-200'
                          : 'bg-gray-100'
                      } ${
                        isNewLabReport(lab)
                          ? 'w-fit !bg-white border-river-blue border-[1px]'
                          : 'w-[130px]'
                      } text-[13px] rounded-full py-[3px] px-5 items-center
                      flex-shrink-0 text-ellipsis overflow-hidden whitespace-nowrap text-center `}
                    >
                      {lab.status === LAB_TEST_STATUS.REQUESTED
                        ? 'Requested'
                        : lab.status === LAB_TEST_STATUS.RECEIVED_REPORT && lab.url
                        ? 'Received report'
                        : 'Sent to lab'}
                    </div>
                    <div className="laptop:flex items-center hidden ml-[15%]">
                      <Icon id="iconArrowForward" className="w-4 group-hover:text-river-black" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {labs.length > 6 && labsLimit === 6 && (
          <div className="text-center pb-1 pt-3 text-river-blue">
            <button
              onClick={() => setLabsLimit(labs.length)}
              type="button"
              className="p-2 !font-medium"
            >
              Show more labs...
            </button>
          </div>
        )}
      </div>

      {!(isFetching && isLoading) && (
        <button
          type="button"
          onClick={() => handleNextTab()}
          className="w-fit border border-river-blue text-river-blue rounded-full px-5 py-[9px] text-sm mt-6 min-w-[180px] 
            hover:bg-[#EEFBFF]"
        >
          Next
        </button>
      )}

      {labDetails && <LabDetails lab={labDetails} onCloseModal={() => setLabDetails(undefined)} />}
    </>
  );
}

export default Labs;
